import { createSelector } from 'reselect';
import { RootState } from '../../../../types';
import { EntityTypes } from '../../../../constants/entities';
import { IActionRegister, IHistoryItem, IRegisterReducer, RegisterResult } from 'core/slices/register';
import { IEntityReducer } from '../../../reducers/entities';
import { selectHistory } from '../history';
import { ActionStatus, ActionType } from 'types/domain/action';
import type { INodeProps } from 'core/selectors/admin/flow';
import * as adminFlow from 'core/selectors/admin/flow';
import { reduceArrayById } from 'utils/reduceArrayById';
import { blameInputs, noteRecompute, timeRecompute } from 'core/middleware/perf';

const entity = (state: RootState) => state.entity.present;
// Narrowed inputs: these selectors sit under the action feed, so every extra
// input they depend on rebuilds the whole feed — and re-renders every slide.
const attempts = (state: RootState) => state.register.attempts;
const courseProgressId = (state: RootState) => state.state[EntityTypes.COURSE_PROGRESS];
const selectedActionId = (state: RootState) => state.state[EntityTypes.ACTION];
const history = selectHistory;

export type ActionHistory = Record<number, ActionHistoryItem[]>;
export type ActionHistoryItem = {
  actionId: number;
  actionRegisterId: string;
  /** How the action was answered — drives correct/wrong branching in the feed. */
  result?: RegisterResult;
  /** Entities the learner selected, for author-wired per-answer branches. */
  selectedEntityIds?: number[];
};

export const selectActionHistory = createSelector(
  [entity, history, attempts, courseProgressId],
  (entity, history, attempts, courseProgressId) => {
    blameInputs(
      'selectActionHistory',
      ['entity', 'history', 'attempts', 'courseProgressId'],
      [entity, history, attempts, courseProgressId],
    );
    return timeRecompute('selectActionHistory', () => parseActionHistory(entity, history, attempts, courseProgressId));
  },
);

export function parseActionHistory(
  entity: IEntityReducer,
  history: IHistoryItem[],
  attempts: IRegisterReducer['attempts'],
  courseProgressId: number,
) {
  return history
    .filter(hist => hist.courseProgressId === courseProgressId && attempts[hist.sessionAttemptId])
    .reduce((prev, hist: IHistoryItem) => {
      prev[hist.sessionAttemptId] = Object.values(attempts[hist.sessionAttemptId].actions)
        .filter(
          a =>
            !!entity[EntityTypes.ACTION][a.actionId] &&
            entity[EntityTypes.ACTION][a.actionId].status !== ActionStatus.DELETED,
        )
        .sort((a, b) => (a.created < b.created ? -1 : 1))
        .map((ar: IActionRegister) => {
          return {
            actionId: ar.actionId,
            actionRegisterId: ar.actionRegisterId,
            result: ar.result,
            selectedEntityIds: Object.values(ar.entities ?? {})
              .filter(ent => ent.selected)
              .map(ent => ent.id),
          };
        });
      return prev;
    }, {} as ActionHistory);
}

// `flow.selectedNode` and `session.mode` used to be inputs here but were never
// read — they only forced a recompute (and a full feed/slide rebuild) on every
// node selection.
export const selectSimulatedActionHistory = createSelector(
  // Deferred lookup: this module and `admin/flow` sit in an import cycle
  // (flow -> action -> scene -> sceneConfig -> actionFeed -> actionHistory -> flow).
  // Referencing the `const` selector directly at module-eval time hits its TDZ
  // whenever the cycle is entered from this side — which HMR does on every edit
  // to a module that pulls the feed in first.
  [selectedActionId, (state: RootState) => adminFlow.selectModuleNodesAndEdges(state)],
  (id, moduleNodesAndEdges) => {
    blameInputs('selectSimulatedActionHistory', ['selectedActionId', 'moduleNodesAndEdges'], [id, moduleNodesAndEdges]);
    noteRecompute('selectSimulatedActionHistory');
    if (!moduleNodesAndEdges) return EMPTY_HISTORY;

    const actionTree = [];
    const actions = moduleNodesAndEdges.nodes
      .filter(n => ![ActionType.FUTURE].includes(n.data.type as ActionType))
      .map(n => n.data);
    const actionsById = reduceArrayById(actions);

    buildEditHistory(id, actions, actionTree);

    let history = actionTree.reverse();

    return reuseSimulated(
      history.map(actionId => ({ actionId, actionType: actionsById[actionId]?.type, actionRegisterId: null })),
    );
  },
);

const EMPTY_HISTORY: SimulatedHistoryItem[] = Object.freeze([]) as unknown as SimulatedHistoryItem[];

type SimulatedHistoryItem = { actionId: number; actionType: ActionType; actionRegisterId: string };

/**
 * `selectModuleNodesAndEdges` is a cached selector whose result changes identity
 * far more often than it actually changes content — re-reselect hands back a
 * different cache entry whenever the module-id key flips. That made this
 * selector rebuild constantly, and since it feeds the action feed, it rebuilt
 * the feed (and re-rendered every slide tile) each time.
 *
 * The content is almost always identical, so hand back the previous array
 * whenever it matches and let `React.memo` downstream hold. Same trick as
 * `reuseFeedItems` in actionFeed.ts.
 */
let previousSimulated: SimulatedHistoryItem[] = [];

function reuseSimulated(next: SimulatedHistoryItem[]): SimulatedHistoryItem[] {
  const previous = previousSimulated;
  const identical =
    next.length === previous.length &&
    next.every(
      (item, i) =>
        previous[i].actionId === item.actionId &&
        previous[i].actionType === item.actionType &&
        previous[i].actionRegisterId === item.actionRegisterId,
    );

  previousSimulated = identical ? previous : next;
  return previousSimulated;
}

const buildEditHistory = (actionId: number, nodes: INodeProps[], history: number[]) => {
  history.push(actionId);
  const index = nodes.findIndex(n => n.id === actionId);

  if (nodes[index]?.parentActionId) buildEditHistory(nodes[index]?.parentActionId, nodes, history);
  else if (index > 0) buildEditHistory(nodes[index - 1]?.id, nodes, history);
};
